import { FieldInputValue, InputFieldProps } from 'components/Forms';
import {
  AdjustmentType,
  DayOfWeek,
  ScheduleAdjustmentReason
} from 'models/Adjustment';
import AdjustmentGroup from 'models/AdjustmentGroup';
import { getAdjustmentChange, getAdjustmentForDay } from 'utils/adjustments';
import {
  TemporaryAdjustmentConstants,
  atLeastOneFilledSchema,
  getEfficiencyOptions
} from 'views/Engineers/utils';
import * as Yup from 'yup';
import {
  adjustmentCommonFieldsInitialValues,
  adjustmentCommonFieldsValidationSchema,
  adjustmentWeekdayKeys,
  getAdjustmentCommonFields
} from './adjustmentFormCommonConfig';

export const getDayFieldName = (day: string) => `efficiencyChange${day}`;

const getEfficiencyFieldForWeekday = (day: string): InputFieldProps => {
  const fieldName = getDayFieldName(day);
  return {
    id: fieldName,
    name: fieldName,
    qaId: fieldName,
    disablePlaceholder: true,
    inputType: 'select',
    options: getEfficiencyOptions()
  };
};

const efficiencyReasonOptions = (): FieldInputValue[] => {
  return [
    {
      value: ScheduleAdjustmentReason.Bybox,
      label: ScheduleAdjustmentReason.Bybox,
      key: 'byBox'
    },
    {
      value: ScheduleAdjustmentReason.CompetencyRemoval,
      label: ScheduleAdjustmentReason.CompetencyRemoval,
      key: 'competencyRemoval'
    },
    {
      value: ScheduleAdjustmentReason.EvCharging,
      label: ScheduleAdjustmentReason.EvCharging,
      key: 'evCharging'
    },
    {
      value: ScheduleAdjustmentReason.FixedAppointmentWorkaround,
      label: ScheduleAdjustmentReason.FixedAppointmentWorkaround,
      key: 'fixedAppointmentWorkaround'
    },
    {
      value: ScheduleAdjustmentReason.Mentoring,
      label: ScheduleAdjustmentReason.Mentoring,
      key: 'mentoring'
    },
    {
      value: ScheduleAdjustmentReason.Mobilisation,
      label: ScheduleAdjustmentReason.Mobilisation,
      key: 'mobilisation'
    },
    {
      value: ScheduleAdjustmentReason.NewStarter,
      label: ScheduleAdjustmentReason.NewStarter,
      key: 'newStarter'
    },
    {
      value: ScheduleAdjustmentReason.ReducedWorkloadForPersonalMedicalReasons,
      label: ScheduleAdjustmentReason.ReducedWorkloadForPersonalMedicalReasons,
      key: 'reducedWorkloadForPersonalMedicalReasons'
    },
    {
      value: ScheduleAdjustmentReason.SaturdayWorking,
      label: ScheduleAdjustmentReason.SaturdayWorking,
      key: 'saturdayWorking'
    },
    {
      value: ScheduleAdjustmentReason.MissedAppointmentPerformance,
      label: ScheduleAdjustmentReason.MissedAppointmentPerformance,
      key: 'missedAppointmentPerformance'
    },
    {
      value: ScheduleAdjustmentReason.EVInstallations,
      label: ScheduleAdjustmentReason.EVInstallations,
      key: 'eVInstallations'
    },
    {
      value: ScheduleAdjustmentReason.ReservedCapacity,
      label: ScheduleAdjustmentReason.ReservedCapacity,
      key: 'reservedCapacity'
    },
    {
      value: ScheduleAdjustmentReason.InDayAdjustment,
      label: ScheduleAdjustmentReason.InDayAdjustment,
      key: 'inDayAdjustment'
    }
  ];
};

export const efficiencyFormCommonFields = getAdjustmentCommonFields(
  efficiencyReasonOptions()
);

export const efficiencyFormWeekdayFields = Object.fromEntries(
  adjustmentWeekdayKeys.map(day => [day, getEfficiencyFieldForWeekday(day)])
);

export const efficiencyFormValidationSchema = Yup.object()
  .shape(adjustmentCommonFieldsValidationSchema)
  .test('atLeastOneFilled', function validOneShiftPattern(formValues) {
    return atLeastOneFilledSchema(
      formValues,
      getDayFieldName,
      this.createError
    );
  });

const getWeekdayEfficiencyFromGroup = (
  day: DayOfWeek,
  adjustmentGroup: AdjustmentGroup
) => {
  const adjustment = getAdjustmentForDay(day, adjustmentGroup.adjustments);
  return adjustment
    ? getAdjustmentChange(adjustment, AdjustmentType.Efficiency)?.change
    : undefined;
};

export const efficiencyFormWeekdaysInitialValues = (
  adjustmentGroup: AdjustmentGroup | undefined
) => {
  const fieldNameEfficiencyPairs = adjustmentWeekdayKeys.map(day => {
    const adjustmentGroupEfficiency = adjustmentGroup
      ? getWeekdayEfficiencyFromGroup(
          day.toUpperCase() as DayOfWeek,
          adjustmentGroup
        )
      : null;

    return [
      getDayFieldName(day),
      adjustmentGroupEfficiency || TemporaryAdjustmentConstants.Empty
    ];
  });

  return {
    ...Object.fromEntries(fieldNameEfficiencyPairs)
  };
};

export const efficiencyFormInitialValues = (
  adjustmentGroup?: AdjustmentGroup
) => {
  const efficiencyDaysInitialValues = efficiencyFormWeekdaysInitialValues(
    adjustmentGroup
  );

  return {
    ...adjustmentCommonFieldsInitialValues(adjustmentGroup?.adjustments[0]),
    ...efficiencyDaysInitialValues
  };
};
